import React from "react";
import styled from "styled-components";
import { PropertyForm, PropertyFormData } from "../form/PropertyForm";
import { usePropertyEdit, usePropertyToast } from "../../pages/property/hook";
import { Toast } from "../../atoms/Toast";
import { ModalTemplate } from "../../templates/ModalTemplate";
import { PropertyEditRequestBody } from "../../../core/api/requests/property/edit/types";

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onSubmit?: (data: PropertyFormData) => void;
    onSuccess?: () => void;
    defaultValues?: Partial<PropertyFormData>;
    propertyId?: string;
    refetchProperties: () => void;
}

export const EditPropertyModal: React.FC<Props> = ({
    isOpen,
    onClose,
    onSubmit,
    onSuccess,
    defaultValues,
    propertyId,
    refetchProperties
}) => {
    const { toast, showToast, hideToast } = usePropertyToast();
    const { handleEditProperty } = usePropertyEdit(showToast, refetchProperties, onClose);

    const handleFormSubmit = async (
        data: PropertyFormData,
        onError: (message: string) => void
    ): Promise<void> => {
        if (!propertyId) {
            onError("Property ID is missing.");
            return;
        }

        // Check if there are any images (new uploads or existing ones not deleted)
        const newImages = data.images.filter(img => img instanceof File);
        const existingImages = defaultValues?.images || [];
        const deletedIndices = data.deletedImageIndices || [];
        const remainingExistingImages = existingImages.length - deletedIndices.length;
        const totalImages = newImages.length + remainingExistingImages;

        if (totalImages === 0) {
            onError("At least one property image is required.");
            return;
        }

        const featureIds = Array.isArray(data.features)
            ? data.features.map((f: any) => {
                if (typeof f === "object" && f !== null) {
                    if ("id" in f) return parseInt(f.id, 10);
                    if ("value" in f) return parseInt(f.value, 10);
                }
                return parseInt(f, 10);
            }).filter(id => !isNaN(id)) // Filter out any NaN values
            : [];

        // Prepare the base data object
        const baseData = {
            property_name: data.propertyName,
            property_category_id: data.propertyCategory,
            property_type_id: data.propertyType,
            property_status: data.propertyStatus,
            owner_id: data.owner,
            city_id: data.city,
            district_id: data.district,
            commune_id: data.commune,
            price: data.price,
            size: data.size,
            land_area: data.landArea,
            bedrooms: data.bedrooms,
            bathrooms: data.bathrooms,
            popular_place_id: data.popularPlace,
            youtube_link: data.linkYoutube,
            google_map_link: data.googleMapLink,
            description: data.description,
            property_images: data.images.filter(img => img instanceof File), // Only send File objects
            is_available: data.isAvailable ? "true" : "false",
            is_hot: data.isHot ? "true" : "false",
        };

        // Include feature_ids even if empty to allow clearing
        const baseDataWithFeatures = { ...baseData, feature_ids: featureIds };


        // Only include thumbnail_image if user uploaded a new file
        const editDataWithThumbnail = data.coverImage instanceof File
            ? { ...baseDataWithFeatures, thumbnail_image: data.coverImage }
            : baseDataWithFeatures;

        // Include deleted image indices if any images were deleted
        const finalEditData: PropertyEditRequestBody = {
            ...editDataWithThumbnail,
            feature_ids: featureIds.map(id => String(id)),
            ...(data.deletedImageIndices && data.deletedImageIndices.length > 0 && { deleted_image_indices: data.deletedImageIndices }),
            ...(data.deleteCoverImage && { delete_thumbnail: 'true' })
        };

        return new Promise<void>((resolve, reject) => {
            handleEditProperty(
                finalEditData,
                propertyId,
                (errorMessage) => {
                    onError(errorMessage);
                    reject(new Error(errorMessage));
                },
                () => {
                    onSubmit?.(data);
                    onSuccess?.();
                    resolve();
                }
            );
        });
    }

    if (!isOpen) return null;
    return (
        <div>
            {toast.visible && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={hideToast}
                />
            )}
            <ModalTemplate
                isOpen={isOpen}
                title="Update Property"
                onClose={onClose}
                maxWidth="95vw"
            >
                <ResponsiveWrapper>
                    <PropertyForm handleSubmit={handleFormSubmit} defaultValues={defaultValues} mode="edit" />
                </ResponsiveWrapper>
            </ModalTemplate>
        </div>
    )
}

const ResponsiveWrapper = styled.div`
  @media (max-width: 768px) {
    padding: 0 8px;
  }
  
  @media (max-width: 480px) {
    padding: 0 4px;
  }
`;